import React, { useCallback, useContext, useRef } from 'react';
import styled from 'styled-components';

import { Months } from '../types';
import { GanttGrid } from './styled-gantt.components';
import { MonthCell } from './month-cell';

import { GanttChartContext } from '../context/gantt-chart.context';
import { getChartGradient } from './chart';
import { useIntersectionObserver } from 'usehooks-ts';
import { observer } from 'mobx-react-lite';

export const ChartHeader: React.FC = observer(() => {
  const {
    chartStartDate,
    datesRangeByMonths,
    chartContainerRef,
    extendEndRange,
    extendStartRange,
  } = useContext(GanttChartContext);
  const headerRef = useRef<HTMLDivElement | null>(null);

  const containerCallback = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node || !chartStartDate) {
        return null;
      }

      if (headerRef) {
        headerRef.current = node;
      }

      const startDay = chartStartDate.getDay();

      const gradientString = getChartGradient(startDay);

      node.style.background = `
        repeating-linear-gradient(90deg, ${gradientString}),
        repeating-linear-gradient(
          to bottom,
          transparent 0rem 0.3rem,
          var(--color-surfaces-bg-elevation-2) 0.3rem 0.5rem
        ),
        repeating-linear-gradient(
          90deg,
          transparent 0rem 4.7rem,
          var(--color-surfaces-bg-elevation-4) 4.7rem 4.8rem
        )`;

      node.style.backgroundSize = `
        336px 100%, 
        100% 0.5rem, 
        4.8rem 100%`;

      node.style.backgroundPosition = '0 0, 0 0, 0 0';

      node.style.willChange = 'transform';
      node.style.transform = 'translateZ(0)';
      node.style.backfaceVisibility = 'hidden';
    },
    [chartStartDate],
  );

  const { ref: extendStartRef } = useIntersectionObserver({
    rootMargin: '0px  300px  0px 0px',
    onChange: async (isIntersecting, entry) => {
      if (isIntersecting) {
        const { daysCount } = (await extendStartRange?.(6)) || {};

        if (chartContainerRef?.current && daysCount && headerRef.current) {
          chartContainerRef.current.scrollLeft =
            headerRef.current.scrollLeft + (daysCount + 1) * 48;
        }
      }
    },
  });

  const { ref: extendEndRef } = useIntersectionObserver({
    rootMargin: '0px 300px 0px 0px',
    onChange: (isIntersecting) => {
      if (isIntersecting) {
        extendEndRange?.(6);
      }
    },
  });

  if (!datesRangeByMonths) {
    return null;
  }

  const monthsKeys = Object.keys(datesRangeByMonths).sort((a, b) => {
    const dateA = new Date(a);
    const dateB = new Date(b);
    return dateA.getTime() - dateB.getTime();
  });

  return (
    <Container ref={containerCallback}>
      {monthsKeys.map((monthKey, keyIndex) => {
        const monthDates = datesRangeByMonths[monthKey];

        const monthIndex = monthDates[0].month;
        const year = monthDates[0].year;
        const monthName = Months[monthIndex];

        return (
          <MonthContainer
            key={monthKey}
            ref={
              keyIndex === monthsKeys.length - 6
                ? extendEndRef
                : keyIndex === 0
                ? extendStartRef
                : null
            }
          >
            <MonthLabel>
              <StickyLabel>
                {monthName} {year}
              </StickyLabel>
            </MonthLabel>

            <GanttGrid>
              {monthDates?.map((date) => {
                const { day, month, year, isToday, weekDay, isWeekend } = date;

                return (
                  <MonthCell
                    key={`${day}-${month}-${year}`}
                    day={day}
                    month={month}
                    year={year}
                    isToday={isToday}
                    weekDay={weekDay}
                    isWeekend={isWeekend}
                  />
                );
              })}
            </GanttGrid>
          </MonthContainer>
        );
      })}
    </Container>
  );
});

ChartHeader.displayName = 'ChartHeader';

const Container = styled.div`
  position: relative;
  display: flex;
  height: fit-content;
  width: fit-content;
  pointer-events: none;
`;

const MonthLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 0.6rem 1.2rem;
  position: relative;
  width: 100%;
  background-color: var(--color-surfaces-bg-elevation-3);
  font-size: 1.2rem;
  line-height: 1.6rem;
  font-weight: 400;
  color: var(--color-texts-high-contrast);
  border-inline-end: 0.1rem solid var(--color-surfaces-bg-elevation-4);
  white-space: nowrap;

  & > span:last-child {
    color: var(--color-texts-middle-contrast);
  }
`;

const StickyLabel = styled.span`
  position: sticky;
  left: 0.8rem;
`;

const MonthContainer = styled.div`
  position: relative;
  background: transparent;

  &:first-child {
    & ${MonthLabel} {
      border-radius: 0.4rem 0 0 0;
    }
  }

  &:last-child {
    & ${MonthLabel} {
      border-radius: 0 0.4rem 0 0;
      border: none;
    }
  }
`;
